import { ReactNode } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import { flattenNavItems, isNavGroup, TNavEntry } from 'routes/constants';
import { isPathActive } from '../NavGroupButton/utils';

// A single clickable row of a dropdown. A nested group is collapsed into one
// row that navigates to its first (allowed) leaf; `leafPaths` holds every leaf
// path underneath so the row is highlighted whichever of them is active.
export type TNavMenuItem = {
	name: string;
	path: string;
	leafPaths: string[];
};

// Maps the direct children of a group to menu rows, collapsing nested groups.
// Nested levels exist for permissions (e.g. per-tab access) and are not
// rendered as deeper menus.
export const toNavMenuItems = (entries: TNavEntry[]): TNavMenuItem[] =>
	entries.flatMap((entry): TNavMenuItem[] => {
		if (!isNavGroup(entry))
			return [{ name: entry.name, path: entry.path, leafPaths: [entry.path] }];
		const leaves = flattenNavItems(entry.items);
		if (leaves.length === 0) return [];
		return [{ name: entry.name, path: leaves[0].path, leafPaths: leaves.map((leaf) => leaf.path) }];
	});

type TRenderNavMenuItemsParams = {
	items: TNavMenuItem[];
	locationPathname: string;
	onItemClick: (path: string) => void;
	// Builds a DOM id for each row. Used to target rows from the tour.
	getItemId?: (item: TNavMenuItem) => string | undefined;
	// Prefix for React keys so the same rows can be rendered in several menus.
	keyPrefix?: string;
	// Extra left padding (in theme spacing units) for rows listed under a subheader.
	indent?: number;
};

export const renderNavMenuItems = ({
	items,
	locationPathname,
	onItemClick,
	getItemId,
	keyPrefix = '',
	indent = 0,
}: TRenderNavMenuItemsParams): ReactNode[] => {
	// Only the most specific matching row is highlighted, so "/invoice-management" is not
	// highlighted together with its sibling "/invoice-management/credit-notes".
	const getMatchDepth = (item: TNavMenuItem): number =>
		Math.max(
			-1,
			...item.leafPaths
				.filter((path) => isPathActive(locationPathname, path))
				.map((path) => path.split('?')[0].split('/').filter(Boolean).length),
		);
	const activeDepth = Math.max(-1, ...items.map(getMatchDepth));
	return items.map((item) => {
		const isActive = activeDepth >= 0 && getMatchDepth(item) === activeDepth;
		return (
			<MenuItem
				key={`${keyPrefix}${item.path}`}
				id={getItemId?.(item)}
				onClick={() => onItemClick(item.path)}
				sx={{
					'&.MuiMenuItem-root': {
						color: isActive ? '#5aa1bf' : 'primary.main',
						fontWeight: 550,
						fontSize: '0.8rem',
						lineHeight: '1.2rem',
						justifyContent: 'flex-start',
						pl: 2 + indent,
					},
				}}
			>
				{item.name}
			</MenuItem>
		);
	});
};

type TRenderNavMenuEntriesParams = Omit<TRenderNavMenuItemsParams, 'items' | 'indent'> & {
	entries: TNavEntry[];
};

// Renders top-level nav entries as a flat menu: a group becomes a subheader
// followed by its (collapsed) rows, a plain item becomes a single row.
// Returns an array (not a Fragment) because MUI `Menu` requires direct children.
export const renderNavMenuEntries = ({
	entries,
	keyPrefix = '',
	...rest
}: TRenderNavMenuEntriesParams): ReactNode[] =>
	entries.flatMap((entry) => {
		if (isNavGroup(entry)) {
			return [
				<ListSubheader
					key={`${keyPrefix}subheader-${entry.name}`}
					sx={{
						lineHeight: '1.6rem',
						fontWeight: 700,
						fontSize: '0.7rem',
						textTransform: 'uppercase',
						color: 'text.secondary',
					}}
				>
					{entry.name}
				</ListSubheader>,
				...renderNavMenuItems({
					items: toNavMenuItems(entry.items),
					keyPrefix,
					indent: 1,
					...rest,
				}),
			];
		}
		return renderNavMenuItems({ items: toNavMenuItems([entry]), keyPrefix, ...rest });
	});
